interface GroupByFunction<T, K extends PropertyKey> {
  (item: T): K;
}
/**
 * Groups an array of items by a specified key or function.
 *
 * @param array - The array of items to group.
 * @param iteratee - A function that returns the key to group by, or a property key.
 * @returns A record where the keys are the unique values returned by the iteratee, and the values are arrays of items that share that key.
 *
 * @example
 * ```ts
 * const people = [
 *   { name: 'Alice', age: 25 },
 *   { name: 'Bob', age: 30 },
 *   { name: 'Charlie', age: 25 },
 *   { name: 'David', age: 30 },
 * ];
 *
 * const groupedByAge = groupBy(people, 'age');
 *
 * // Output:
 * {
 *   '25': [
 *     { name: 'Alice', age: 25 },
 *     { name: 'Charlie', age: 25 },
 *   ],
 *   '30': [
 *     { name: 'Bob', age: 30 },
 *     { name: 'David', age: 30 },
 *   ],
 * }
 */
export function groupBy<T>(array: T[], key: keyof T): Record<PropertyKey, T[]>;
export function groupBy<T, K extends PropertyKey>(
  array: T[],
  iteratee: GroupByFunction<T, K>,
): Record<K, T[]>;
export function groupBy<T, K extends PropertyKey>(
  array: T[],
  iteratee: keyof T | GroupByFunction<T, K>,
): Record<PropertyKey, T[]> {
  return array.reduce(
    (result, item) => {
      const key = typeof iteratee === 'function' ? iteratee(item) : item[iteratee as keyof T];

      if (!result[key as PropertyKey]) {
        result[key as PropertyKey] = [];
      }

      result[key as PropertyKey].push(item);

      return result;
    },
    {} as Record<PropertyKey, T[]>,
  );
}
